import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Camera, 
  Video, 
  Upload, 
  CheckCircle2, 
  XCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Clock,
  Trash2,
  RotateCcw,
  Eye,
  Calendar,
  Home
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const TenantInspectionScan = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  // State
  const [session, setSession] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [currentRoomIndex, setCurrentRoomIndex] = useState(0);
  const [captures, setCaptures] = useState({});
  const [isCapturing, setIsCapturing] = useState(false);
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [deadlineInfo, setDeadlineInfo] = useState(null);
  
  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Initialize
  useEffect(() => {
    loadInspectionTask();
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [propertyId]);
  
  const loadInspectionTask = async () => {
    try {
      setLoading(true);
      
      // Get inspection task with baseline info
      const taskResponse = await fetch(`${API_URL}/api/scanning/inspection/task/${propertyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!taskResponse.ok) {
        const data = await taskResponse.json();
        throw new Error(data.detail || 'Failed to load inspection task');
      }
      
      const taskData = await taskResponse.json();
      setBaseline(taskData.baseline);
      setDeadlineInfo(taskData.deadline_info);
      
      // Extract rooms from baseline room graph
      const roomGraph = taskData.room_graph || {};
      const roomList = (roomGraph.rooms || []).map(r => r.name);
      setRooms(roomList);
      
      // Check for pending session or create new one
      if (taskData.pending_session) {
        setSession(taskData.pending_session);
        // Initialize captures from existing session
        const initialCaptures = {};
        roomList.forEach(room => {
          initialCaptures[room] = [];
        });
        setCaptures(initialCaptures);
      } else {
        // Create new inspection session
        await createSession(roomList);
      }
      
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const createSession = async (roomList) => {
    try {
      const response = await fetch(`${API_URL}/api/scanning/inspection/sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          property_id: propertyId,
        }),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create inspection session');
      }
      
      const sessionData = await response.json();
      setSession(sessionData);
      
      // Initialize captures
      const initialCaptures = {};
      (sessionData.required_rooms || roomList).forEach(room => {
        initialCaptures[room] = [];
      });
      setCaptures(initialCaptures);
      
      // Update deadline info
      if (sessionData.capture_window_end) {
        const deadline = new Date(sessionData.capture_window_end);
        const now = new Date();
        const remaining = deadline - now;
        setDeadlineInfo({
          deadline: sessionData.capture_window_end,
          days_remaining: Math.max(0, Math.floor(remaining / (1000 * 60 * 60 * 24))),
          hours_remaining: Math.max(0, Math.floor(remaining / (1000 * 60 * 60))),
          is_overdue: remaining < 0,
        });
      }
      
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    }
  };
  
  // Camera controls
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setIsCapturing(true);
      
    } catch (err) {
      toast.error('Failed to access camera. Please ensure camera permissions are granted.');
      console.error('Camera error:', err);
    }
  };
  
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCapturing(false);
  };
  
  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    // Get image data
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    
    // Add to captures
    const currentRoom = rooms[currentRoomIndex];
    setCaptures(prev => ({
      ...prev,
      [currentRoom]: [...(prev[currentRoom] || []), {
        id: Date.now(),
        dataUrl,
        timestamp: new Date().toISOString(),
      }],
    }));
    
    toast.success('Photo captured!');
    
    // Upload to backend
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const formData = new FormData();
      formData.append('file', blob, `inspection_${Date.now()}.jpg`);
      formData.append('room_name', currentRoom);
      formData.append('artefact_kind', 'raw_photo');
      
      await fetch(`${API_URL}/api/scanning/sessions/${session.id}/artefacts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
    } catch (err) {
      console.error('Upload error:', err);
    }
  }, [rooms, currentRoomIndex, session, token]);
  
  const removeCapture = (roomName, captureId) => {
    setCaptures(prev => ({
      ...prev,
      [roomName]: prev[roomName].filter(c => c.id !== captureId),
    }));
  };
  
  // Room navigation
  const goToNextRoom = () => {
    if (currentRoomIndex < rooms.length - 1) {
      setCurrentRoomIndex(currentRoomIndex + 1);
    }
  };
  
  const goToPrevRoom = () => {
    if (currentRoomIndex > 0) {
      setCurrentRoomIndex(currentRoomIndex - 1);
    }
  };
  
  // Progress calculation
  const getProgress = () => {
    const roomsWithCaptures = Object.entries(captures).filter(
      ([_, caps]) => caps.length >= 3
    ).length;
    return (roomsWithCaptures / rooms.length) * 100;
  };
  
  const isRoomComplete = (roomName) => {
    return (captures[roomName]?.length || 0) >= 3;
  };
  
  const canSubmit = () => {
    return rooms.every(room => isRoomComplete(room));
  };
  
  // Submit session
  const handleSubmit = async () => {
    if (!canSubmit()) {
      toast.error('Please capture at least 3 photos per room');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const response = await fetch(`${API_URL}/api/scanning/sessions/${session.id}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Submission failed');
      }
      
      const result = await response.json();
      
      toast.success('Inspection scan submitted successfully!');
      
      // Navigate to results
      navigate(`/scan/results/${session.id}?type=inspection`);
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="inspection-scan-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading inspection task...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-6" data-testid="inspection-scan-error">
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={() => navigate(-1)} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }
  
  const currentRoom = rooms[currentRoomIndex];
  const currentCaptures = captures[currentRoom] || [];
  
  return (
    <div className="min-h-screen bg-emerald-900 text-white" data-testid="inspection-scan-page">
      {/* Header */}
      <div className="bg-emerald-800 border-b border-emerald-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm"
            className="text-white hover:bg-emerald-700"
            onClick={() => {
              stopCamera();
              navigate(-1);
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Exit
          </Button>
          <div className="text-center">
            <h1 className="font-semibold">Property Check</h1>
            <p className="text-xs text-emerald-300">Scan Inspection</p>
          </div>
          <Badge variant={canSubmit() ? 'default' : 'secondary'} className="bg-emerald-600">
            {Math.round(getProgress())}%
          </Badge>
        </div>
        
        {/* Progress bar */}
        <Progress value={getProgress()} className="mt-3 h-1 bg-emerald-700" />
        
        {/* Deadline warning */}
        {deadlineInfo && (
          <div className={`mt-2 px-3 py-2 rounded-lg flex items-center gap-2 ${
            deadlineInfo.is_overdue 
              ? 'bg-red-500/20 border border-red-500/50' 
              : deadlineInfo.days_remaining <= 1 
                ? 'bg-amber-500/20 border border-amber-500/50'
                : 'bg-emerald-700/50'
          }`}>
            <Clock className={`h-4 w-4 ${
              deadlineInfo.is_overdue ? 'text-red-400' : 
              deadlineInfo.days_remaining <= 1 ? 'text-amber-400' : 'text-emerald-400'
            }`} />
            <span className="text-sm">
              {deadlineInfo.is_overdue 
                ? 'Inspection overdue!' 
                : `${deadlineInfo.days_remaining} days remaining`}
            </span>
          </div>
        )}
      </div>
      
      {/* Room Navigation */}
      <div className="bg-emerald-800/50 px-4 py-2 overflow-x-auto">
        <div className="flex gap-2 items-center">
          {rooms.map((room, index) => (
            <button
              key={room}
              onClick={() => setCurrentRoomIndex(index)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                index === currentRoomIndex
                  ? 'bg-emerald-500 text-white'
                  : isRoomComplete(room)
                  ? 'bg-green-600/20 text-green-400 border border-green-600/50'
                  : 'bg-emerald-700 text-emerald-300'
              }`}
            >
              {isRoomComplete(room) && <CheckCircle2 className="h-3 w-3 inline mr-1" />}
              {room}
            </button>
          ))}
        </div>
      </div>
      
      {/* Current Room Info */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{currentRoom}</h2>
            <p className="text-sm text-emerald-300">
              {currentCaptures.length}/3 photos (minimum)
            </p>
          </div>
          <Badge variant={isRoomComplete(currentRoom) ? 'success' : 'secondary'} className={
            isRoomComplete(currentRoom) ? 'bg-green-600' : 'bg-emerald-700'
          }>
            {isRoomComplete(currentRoom) ? 'Complete' : 'In Progress'}
          </Badge>
        </div>
      </div>
      
      {/* Baseline Reference */}
      {baseline && (
        <div className="px-4 mb-3">
          <div className="bg-emerald-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-emerald-300 text-sm">
              <Home className="h-4 w-4" />
              <span>Baseline v{baseline.version} | Quality: {baseline.overall_quality?.toFixed(0) || 'N/A'}%</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Camera View / Captures Grid */}
      <div className="flex-1 px-4">
        {isCapturing ? (
          <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            
            {/* Capture overlay */}
            <div className="absolute inset-0 pointer-events-none">
              {/* Grid lines */}
              <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">
                {[...Array(9)].map((_, i) => (
                  <div key={i} className="border border-white/20" />
                ))}
              </div>
            </div>
            
            {/* Capture button */}
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
              <Button
                size="lg"
                variant="secondary"
                onClick={stopCamera}
              >
                <XCircle className="h-5 w-5" />
              </Button>
              <Button
                size="lg"
                className="w-16 h-16 rounded-full bg-white hover:bg-slate-200"
                onClick={capturePhoto}
              >
                <Camera className="h-8 w-8 text-emerald-900" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Start capture button */}
            <button
              onClick={startCamera}
              className="w-full aspect-[4/3] rounded-xl bg-emerald-800 border-2 border-dashed border-emerald-600 flex flex-col items-center justify-center hover:border-emerald-400 hover:bg-emerald-800/50 transition-all"
            >
              <Camera className="h-12 w-12 text-emerald-400 mb-2" />
              <span className="text-emerald-400">Tap to start camera</span>
            </button>
            
            {/* Captured photos grid */}
            {currentCaptures.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-emerald-300 mb-2">
                  Captured Photos ({currentCaptures.length})
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {currentCaptures.map((capture) => (
                    <div key={capture.id} className="relative aspect-square rounded-lg overflow-hidden group">
                      <img
                        src={capture.dataUrl}
                        alt="Capture"
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => removeCapture(currentRoom, capture.id)}
                        className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Hidden canvas for photo capture */}
      <canvas ref={canvasRef} className="hidden" />
      
      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-emerald-800 border-t border-emerald-700 px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="outline"
            onClick={goToPrevRoom}
            disabled={currentRoomIndex === 0}
            className="border-emerald-600 text-emerald-300 hover:bg-emerald-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
          
          {currentRoomIndex === rooms.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit() || submitting}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600"
            >
              {submitting ? (
                <>Processing...</>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Complete Scan Inspection
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={goToNextRoom}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600"
            >
              Next Room
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
        
        {/* Guidance */}
        <div className="mt-3 p-2 bg-emerald-700/50 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-emerald-300">
              Capture the same areas, corners, and fixtures as shown in the property listing 
              for accurate documentation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantInspectionScan;
